import React from 'react';
import type { PatientDetailsPatientProp } from './internal';
import { PatientDetailsSection } from './internal';
import { intl } from '../../lib';
import { VBox } from '../layout';
import { isEmptyArray } from '../../util';

/** Type of the patient's identifiers. */
type IdentifiersType = PatientDetailsPatientProp['patient']['identifiers'];

/**
 * Props for a {@link PatientDetailsIdentifiers} component.
 */
export type PatientDetailsIdentifiersProps = PatientDetailsPatientProp & {
  /**
   * Optional filter function to apply to the patient's identifiers.
   * If no filter function is present, the entire list of identifiers will be used.
   *
   * @param id the list of identifiers to filter
   * @return the filtered list of identifiers
   */
  readonly filterIdentifiers?: (ids: IdentifiersType) => IdentifiersType;
};

/**
 * Component for displaying a patient's identifier details.
 *
 * @param properties {@link PatientDetailsIdentifiersProps}
 * @return the created {@link React.JSX.Element}.
 * @constructor
 */
export function PatientDetailsIdentifiers({
  patient,
  filterIdentifiers,
  ...props
}: Readonly<PatientDetailsIdentifiersProps>): React.JSX.Element {
  const identifiers =
    filterIdentifiers?.(patient.identifiers) ?? patient.identifiers;

  if (isEmptyArray(identifiers)) {
    return <></>;
  }

  return (
    <PatientDetailsSection
      {...props}
      className="ehs-patient-details-identifiers"
      title={intl('view.patient.identifier')}
    >
      <VBox className="identifiers">
        <>
          {identifiers.map((identifier, index) => (
            // TODO: the ExtJS version also has tooltips of the displayed values with data-qtip
            <React.Fragment key={index}>
              <span className="aa-universal-name">
                <b>{identifier.assigningAuthorityUniversalName}</b>
              </span>
              <span className="identifier-value">
                {identifier.stringRepresentation}
              </span>
            </React.Fragment>
          ))}
        </>
      </VBox>
    </PatientDetailsSection>
  );
}
